import { Stage, Layer, Group, Image } from 'react-konva';
import { useState, useEffect, useRef } from 'react';
import Seat from '../../components/Seat/Seat';
import useActions from './acciones';
import api from '../../helpers/api';
import socket from '../../helpers/socket';
import Loader from '../../components/Loader/Loader';
import { API_URL } from '../../config';

export default function Asientos({ token, zona, funcion = 34 }) {
  const [selectedSeats,   setSelectedSeats]   = useState([]);
  const [seatPositions,   setSeatPositions]   = useState([]);
  const [asientos,        setAsientos]        = useState([]);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [reload,          setReload]          = useState(0);
  const [connected,       setConnected]       = useState(false);
  const [quantity,        setQuantity]        = useState(1);
  const [id,              setId]              = useState(null);
  const [timer,           setTimer]           = useState(null);
  const stageRef = useRef(null);
  // Ref con la selección actual, para leerla desde los listeners de cantidad —
  // que se montan una sola vez y, de otro modo, verían un `selectedSeats` viejo.
  const selectedSeatsRef = useRef(selectedSeats);
  selectedSeatsRef.current = selectedSeats;

  // ── Acciones de socket ────────────────────────────────────────────────────
  const holdSeat = (seatId) => {
    socket.emit('holdSeat', { funcion, seatId });
    if (!timer) {
      localStorage.setItem('woo_cart_timestamp', Date.now().toString());
      setTimer(true);
    }
  };

  const releaseSeat = (seatId) => {
    socket.emit('releaseSeat', { funcion, seatId });
  };

  const { handleSelectSeat } = useActions(
    quantity, setSelectedSeats, selectedSeats, holdSeat, releaseSeat
  );

  // ── Carga de sala al cambiar zona ─────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function getSala() {
      setSelectedSeats([]);

      const salaId = await api.getSalaByNombre(zona, token);

      // Guardia: si la respuesta no es un array válido, abortar
      if (!Array.isArray(salaId) || salaId.length === 0) {
        if (!cancelled) setId(null);
        return;
      }

      if (cancelled) return;
      setId(salaId[0].id);
      setReload(prev => prev + 1);

      const sala = await api.getSala(salaId[0].id, token);

      // Guardia: sala válida tiene un id
      if (!sala?.id) return;

      const asientosData = await api.getAsientosBySala(sala.id, token);
      if (!cancelled && Array.isArray(asientosData)) {
        setAsientos(asientosData);
      }

      // Imagen: soporta data URL (base64) y rutas relativas
      const imgSrc = sala.layout?.startsWith('data:') || sala.layout?.startsWith('http')
        ? sala.layout
        : API_URL + sala.layout;

      const image = new window.Image();
      image.src = imgSrc;
      image.onload = () => { if (!cancelled) setBackgroundImage(image); };
    }

    getSala();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zona]);

  // ── Botón "Agregar al carrito" ────────────────────────────────────────────
  useEffect(() => {
    const handleAddToCartClick = (e) => {
      e.preventDefault();

      if (selectedSeats.length === 0) {
        alert('Seleccioná al menos un asiento antes de continuar.');
        return;
      }

      const form = document.querySelector('form.cart');
      if (!form) return;

      // Limpiar inputs anteriores
      form.querySelectorAll('input[name^="seat_selection"]').forEach(i => i.remove());

      // Crear inputs ocultos para cada asiento seleccionado
      selectedSeats.forEach((seatId, index) => {
        const seat = seatPositions.find(s => s.id === seatId);
        if (seat) {
          const input = document.createElement('input');
          input.type  = 'hidden';
          input.name  = `seat_selection[${index}]`;
          input.value = JSON.stringify({ id: seat.id, row: seat.row, column: seat.column });
          form.appendChild(input);
        }
      });

      // La cantidad que llega al carrito es SIEMPRE el nº de asientos seleccionados.
      // El input nativo de cantidad de WooCommerce se resetea a 1 por re-renders del
      // tema (filtro input_value=1) y no es confiable como fuente de verdad: usar la
      // selección real evita mandar menos butacas de las elegidas y mantiene el precio
      // alineado con la cantidad de entradas.
      const qtyInput = form.querySelector('input[name="quantity"]');
      if (!qtyInput) {
        // Fail-safe: sin el input de cantidad no podemos garantizar que el precio
        // coincida con los asientos elegidos. Abortamos en vez de cobrar de menos/más.
        alert('No se pudo procesar la selección. Recargá la página e intentá de nuevo.');
        return;
      }
      qtyInput.value = selectedSeats.length;

      // ÚNICA navegación: el POST del form. El redirect al carrito lo hace el server
      // (filtro woocommerce_add_to_cart_redirect en cart-management.php) en la
      // respuesta del POST, DESPUÉS de agregar el ítem. Antes acá había un
      // setTimeout(500ms) con window.location.href = '/carro' que corría una carrera
      // contra este mismo POST: en conexiones lentas (teléfonos, Firefox Android) la
      // segunda navegación CANCELABA el POST en vuelo → el ítem nunca se agregaba y
      // el comprador aterrizaba en un carrito vacío. En Chrome desktop con buena red
      // el POST completaba antes de los 500ms y el bug quedaba oculto.
      form.submit();
    };

    const addToCartButton = document.querySelector('.single_add_to_cart_button');
    addToCartButton?.addEventListener('click', handleAddToCartClick);

    // NOTA: se quitó el auto-recorte de selección ("liberar exceso") que existía acá.
    // Reaccionaba a que `quantity` bajara por debajo de los seleccionados y recortaba
    // la selección con slice(-quantity). El problema: el input nativo de cantidad se
    // resetea espuriamente a 1 (re-render del tema), disparando el recorte y dejando
    // 1 solo asiento de los N elegidos sin que el usuario lo note. La selección es
    // ahora la única fuente de verdad; para comprar menos, el usuario deselecciona.

    return () => {
      addToCartButton?.removeEventListener('click', handleAddToCartClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeats, quantity]);

  // ── Fetch estados (al cambiar asientos o al recibir evento de socket) ─────
  useEffect(() => {
    async function getEstados() {
      const estados = await api.getEstados(funcion, token);

      // Guardia: si la respuesta no es un array (ej: error), no actualizar
      if (!Array.isArray(estados)) return;

      const merged = asientos.map(seat => ({
        ...seat,
        estado: estados.find(s => s.asiento === seat.id)?.estado ?? 'Disponible',
      }));
      setSeatPositions(merged);
    }

    // getEstados es una llamada HTTP — no depende del socket.
    // El socket solo sirve para actualizaciones en tiempo real.
    getEstados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asientos, reload]);

  // ── Cantidad de asientos (tema Porto) ─────────────────────────────────────
  useEffect(() => {
    const input    = document.querySelector('input[name="quantity"]');
    const plusBtn  = document.querySelector('.quantity button.plus');
    const minusBtn = document.querySelector('.quantity button.minus');

    if (!input) return;

    // Sincronizar el estado inicial con el valor real del input: el usuario puede
    // haber fijado la cantidad ANTES de que montara este efecto (el tema muta el
    // value del input sin disparar 'input'/'change'), y de otro modo `quantity`
    // quedaría clavado en 1 aunque el stepper muestre 2+.
    setQuantity(Number(input.value) || 1);

    const updateQuantity  = () => setQuantity(Number(input.value));
    const delayedUpdate   = () => setTimeout(updateQuantity, 50);

    // El botón "−" es una acción DELIBERADA de bajar la cantidad: si quedan más
    // asientos seleccionados que la nueva cantidad, soltamos el/los que sobran
    // (los más viejos, conservando los últimos). Se ata al CLICK del botón, no a
    // cualquier cambio de `quantity`, para que un reset espurio del input nunca
    // deseleccione butacas por su cuenta (esa era la raíz de la pérdida silenciosa).
    const handleMinus = () => setTimeout(() => {
      const newQty = Number(input.value);
      setQuantity(newQty);
      const current = selectedSeatsRef.current;
      if (newQty >= 1 && current.length > newQty) {
        const keep = current.slice(-newQty);                       // últimos newQty
        const drop = current.slice(0, current.length - newQty);    // sobrantes
        drop.forEach(seat => releaseSeat(seat));
        setSelectedSeats(keep);
      }
    }, 50);

    input.addEventListener('input',  updateQuantity);
    input.addEventListener('change', updateQuantity);
    plusBtn?.addEventListener('click',  delayedUpdate);
    minusBtn?.addEventListener('click', handleMinus);

    return () => {
      input.removeEventListener('input',  updateQuantity);
      input.removeEventListener('change', updateQuantity);
      plusBtn?.removeEventListener('click',  delayedUpdate);
      minusBtn?.removeEventListener('click', handleMinus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Socket ────────────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      // Si el socket ya estaba conectado cuando este componente montó
      // (race condition: 'connect' ya disparó antes de que agregáramos el listener),
      // seteamos el estado directamente en lugar de esperar un evento que no va a llegar.
      if (socket.connected) {
        setConnected(true);
        socket.emit('joinShowtimeRoom', funcion);
      }

      socket.emit('joinShowtimeRoom', funcion);

      socket.on('joinedShowtimeRoom', () => {
        console.log('[socket] joined room');
        setConnected(true);
      });

      socket.on('seatHeld', ({ seatId }) => {
        console.log('[socket] seatHeld:', seatId);
        setReload(prev => prev + 1);
      });

      socket.on('seatHeldError', ({ seatId, socketId }) => {
        if (socketId === socket.id) {
          alert(`Error: Asiento ${seatId} ya reservado`);
          setReload(prev => prev + 1);
          setSelectedSeats(prev => prev.filter(s => s !== seatId));
        }
      });

      socket.on('seatReleased', ({ seatId }) => {
        console.log('[socket] seatReleased:', seatId);
        setSelectedSeats(prev => prev.filter(s => s !== seatId));
        setReload(prev => prev + 1);
      });

      // FIX: reason es string en socket.io v4, no objeto
      socket.on('disconnect', (reason) => {
        console.warn('[socket] disconnected:', reason);
        setConnected(false);
      });

      socket.on('connect', () => {
        console.log('[socket] connected');
        setConnected(true);
        socket.emit('joinShowtimeRoom', funcion);
        setReload(prev => prev + 1);
      });

      return () => {
        socket.off('joinedShowtimeRoom');
        socket.off('seatHeld');
        socket.off('seatHeldError');
        socket.off('seatReleased');
        socket.off('disconnect');
        socket.off('connect');
      };
    } catch (e) {
      console.error('[socket] error:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funcion]);

  // ── Render ────────────────────────────────────────────────────────────────
  if (!connected) return <Loader />;

  if (!id) return <p style={{ color: 'red', padding: 12 }}>Sala no encontrada para la zona seleccionada.</p>;

  return (
    <div className="d-flex justify-content-center">
      <Stage
        width={backgroundImage?.width   ?? 800}
        height={backgroundImage?.height ?? 600}
        ref={stageRef}
      >
        <Layer>
          {backgroundImage && (
            <Image
              image={backgroundImage}
              width={backgroundImage.width}
              height={backgroundImage.height}
              listening={false}
            />
          )}

          {/* Asientos no seleccionados */}
          {seatPositions.map(pos =>
            selectedSeats.includes(pos.id) ? null : (
              <Seat
                {...pos}
                key={pos.id}
                number={pos.numero}
                isSelected={false}
                status={pos.estado}
                onSelect={() => {
                  if (pos.estado !== 'Reservado' && pos.estado !== 'Ocupado') {
                    handleSelectSeat(pos.id);
                  }
                }}
              />
            )
          )}

          {/* Asientos seleccionados — capa superior */}
          {selectedSeats.length > 0 && (
            <Group x={0} y={0}>
              {seatPositions.map(pos =>
                selectedSeats.includes(pos.id) ? (
                  <Seat
                    {...pos}
                    key={pos.id}
                    number={pos.numero}
                    isSelected={true}
                    status={pos.estado}
                    onSelect={() => {
                      if (pos.estado !== 'Reservado' && pos.estado !== 'Ocupado') {
                        handleSelectSeat(pos.id);
                      }
                    }}
                  />
                ) : null
              )}
            </Group>
          )}
        </Layer>
      </Stage>
    </div>
  );
}
